import axiosMultisite from "shared/tools/axiosMultisite";
import axios from "axios";
import { impressionListSources, trackingDimensionConstants } from "shared/settings/constants";
import { track_add_to_cart, track_remove_from_cart } from 'shared/services/tracking/basketTrackingService';
import createQueue from 'shared/tools/basketPromiseQueue';

const queue = createQueue();

const apiUrl = '/api/global-basket/';
const setChangeReasonUrl = '/api/v1/change-reason/set-change-reason';
// query URL without using browser cache
const headersConfig = {
	'Cache-Control': 'no-cache',
	'Pragma': 'no-cache',
	'Expires': '0',
};

export default {
    state: () => ({
        basket: {},
		trackingBasket: {},
        basketApiCulture: '',
		basketDebounceTime: 0,
		showBasketSkeleton: true,
		flyOutBasketActive: false,

		e1TaxCalculate: {
			isCalculateWatcherInited: false,
			taxParams: null,
			isE1TaxLoading: false,
			abortController: null,
		},
		mediLinkPartialPaymentCalculate: {
			apiUrl: "",
			isLoading: false,
			apiError: null,
			abortController: null,
			paymentData: null,
		},
		discountCode: {
			couponName: '', // input state
		},
		basketIsDirty: true,
		_changeQuantityRequestNo: 0,
    }),
    getters: {
        getBasket: (state) => {
            return state.basket;
        },
		getIsBasketSet: (_, getters) => {
			return Object.keys(getters.getBasket).length !== 0;
        },
		getTrackingBasket: (state) => {
			return {
				ecommerce: state.trackingBasket,
			};
		},
		getBasketOrderType: (_state, getters) => {
			const basket = getters.getBasket;
			const paidProducts = basket.paid?.length;
			const sampleProducts = basket.samples?.length;
			const hasPaidProducts = typeof paidProducts !== "undefined" && paidProducts > 0;
			const hasSampleProducts = typeof sampleProducts !== "undefined" && sampleProducts > 0;

			if (typeof basket === "undefined" || (!hasPaidProducts && !hasSampleProducts)) return trackingDimensionConstants.NO_DATA;

			const isHybrid = hasPaidProducts && hasSampleProducts;
			if (isHybrid) return trackingDimensionConstants.HYBRID;
			return hasPaidProducts ? trackingDimensionConstants.BUYING : trackingDimensionConstants.SAMPLING;
		},
        getBasketItemCount: (state) => {
            if (state.basket?.paid && state.basket?.samples) {
                let count = 0;
                state.basket.paid.forEach((paidItem) => {
                    count += paidItem.quantity;
                });
                state.basket.samples.forEach((sampleItem) => {
                    count += sampleItem.quantity;
                });
                return count;
            }
            return 0;
        },
        getAnyOfMyProductsInBasket: (state) => {
            return state.basket?.paid?.some(item => item.lineItemSource === impressionListSources.MY_PRODUCTS) || false;
        },
		getBasketDebounceTime: (state) => {
			return state.basketDebounceTime;
		},
		getShowBasketSkeleton: (state) => {
			return state.showBasketSkeleton;
		},
		getFlyOutBasketActive: (state) => state.flyOutBasketActive,

		getE1TaxCalculate: (state) => state.e1TaxCalculate,
		getMediLinkPartialPaymentCalculate: (state) => state.mediLinkPartialPaymentCalculate,
		getReimbursableItems: (state) => state.basket?.reimburseable || [],
		getTotalQuantityOfReimbursableItems: (_, getters) => {
			return getters.getReimbursableItems.reduce((total, item) => total + item.quantity, 0);
		},
		getDiscountCode: (state) => state.discountCode,
    },
    mutations: {
        setBasket: (state, basket) => {
            if (basket.samples) {
                for (let sample of basket.samples) {
                    sample.isSample = true;
                    sample.id = sample.code + '-sample';
                }
            }
            state.basket = basket;
        },
		setBasketItemQuantity: (
			state,
			{
				code,
				type, // 'outOfPocket', 'complimentary'
				quantity,
			}
		) => {
			const setQuantity = (items) => {
				const item = items?.find((item) => item.code === code);
				if (item) {
					item.quantity = quantity;
				}
			};

			setQuantity(state.basket.paid);
			if (type === 'outOfPocket') {
				setQuantity(state.basket.reimburseable);
			} else if (type === 'complimentary') {
				setQuantity(state.basket.complimentary);
			}
		},
		setTrackingBasket: (state, trackingBasket) => {
			state.trackingBasket = trackingBasket;
			if (state.basket?.samples) {
				state.basket.samples.forEach(sample => {
					const trackingItem = state.trackingBasket.products.find(item => item.issample && item.id === sample.code);
					if (trackingItem) {
						sample.brand = trackingItem.brand;
						sample.category = trackingItem.category;
						sample.variant = trackingItem.variant;
						sample.trackingParameters = trackingItem.trackingParameters;
					}
				});
				// Ensure these deep array item modifications are picked up by components
				state.basket.samples = state.basket.samples.slice(0);
			}

			if (state.basket?.paid) {
				state.basket.paid.forEach(paid => {
					const trackingItem = state.trackingBasket.products.find(item => !item.issample && item.id === paid.code);
					if (trackingItem) {
						paid.brand = trackingItem.brand;
						paid.category = trackingItem.category;
						paid.variant = trackingItem.variant;
						paid.trackingParameters = trackingItem.trackingParameters;
					}
				});
				// Ensure these deep array item modifications are picked up by components
				state.basket.paid = state.basket.paid.slice(0);
			}
		},
        setBasketApiCulture: (state, culture) => {
            state.basketApiCulture = culture;
        },
		setBasketDebounceTime: (state, data) => {
			state.basketDebounceTime = data;
		},
		setShowBasketSkeleton: (state) => {
			state.showBasketSkeleton = true;
		},
		setHideBasketSkeleton: (state) => {
			state.showBasketSkeleton = false;
		},
		setBasketIsDirty: (state, value) => {
			state.basketIsDirty = value;
		},
		setFlyOutBasketActive: (state, value) => state.flyOutBasketActive = value,

		setE1TaxCalculate: (state, partialUpdate) => {
			state.e1TaxCalculate = {
				...state.e1TaxCalculate,
				...partialUpdate
			}
		},
		setMediLinkPartialPaymentCalculate: (state, partialUpdate) => {
			state.mediLinkPartialPaymentCalculate = {
				...state.mediLinkPartialPaymentCalculate,
				...partialUpdate,
			};
		},
		setDiscountCode: (state, partialUpdate) => {
			state.discountCode = {
				...state.discountCode,
				...partialUpdate,
			}
		},
		_setChangeQuantityRequestNo: (state, value) => {
			state._changeQuantityRequestNo = value;
		},
    },
    actions: {
        fetchBasket: ({ commit, state }) => {
			if (state.basketIsDirty) {
				// clearing the dirty flag before the request completes ONLY works because the requests are queued. If the queue is removed, then this optimization will no longer work
				commit('setBasketIsDirty', false);
				return queue.add(
					() => {
						return axiosMultisite.get(
							`${apiUrl}basket?language=${state.basketApiCulture}`,
							{
								headers: headersConfig
							}
						)
						.then((response) => {
							commit('setHideBasketSkeleton');
							commit('setBasket', response.data);

							return response.data;
						})
						.catch((error) => {
							commit('setHideBasketSkeleton');
							return error;
						});
					}
				);
				}
			return queue.add(
				() => {
					return Promise.resolve(state.basket);
				}
			);

        },
		trackAddToCart: (_, payload) => {
			track_add_to_cart(payload);
		},
		trackRemoveFromCart: (_, payload) => {
			track_remove_from_cart(payload);
		},
        addItemToBasket: ({ commit, state, dispatch }, payload) => {
			commit('setShowBasketSkeleton');
			return queue.add(() => {
				return axiosMultisite.post(`${apiUrl}additem`, {
					...payload,
					language: state.basketApiCulture
				})
                .then((response) => {
					commit('setHideBasketSkeleton');
                    commit('setBasket', response.data);

					dispatch("trackAddToCart", response.data);

					return response;
                })
				.catch(error => {
					commit('setHideBasketSkeleton');
					return error.response;
				});
			});
        },
		addShortlistedProductsToBasket: ({ commit, state, dispatch }, payload) => {
			commit('setShowBasketSkeleton');
			return queue.add(() => {
				return axiosMultisite.post(`${apiUrl}add-shortlisted-products`, {
					...payload,
					language: state.basketApiCulture
				})
                .then((response) => {
					commit('setHideBasketSkeleton');
					commit('setBasketIsDirty', true);
                    dispatch("fetchBasket");
					return response;
                })
				.catch(error => {
					commit('setHideBasketSkeleton');
					return error.response;
				});
			});
		},
		addQuickSampleToBasket: ({ state }, payload) => {
			const apiUrl = `/api/quicksample/addproductsamples?language=${state.basketApiCulture}`;
			return queue.add(() => {
				return axiosMultisite.post(apiUrl, payload).then((response) => {
					return response.data;
				})
				.catch(error => error.response);
			});
		},
        removeItemFromBasket: ({ commit, state, dispatch }, payload) => {
			return queue.add(() => {
				return axiosMultisite.post(`${apiUrl}deleteitem`, {
					...payload,
					language: state.basketApiCulture
				})
					.then((response) => {
						commit('setBasket', response.data);

						dispatch("trackRemoveFromCart", response.data);

						return response.data;
					});
			});
        },
        changeQuantityInBasket: ({ commit, state, dispatch }, payload) => {
			commit('_setChangeQuantityRequestNo', state._changeQuantityRequestNo + 1);
            const currentChangeQuantityRequestNo = state._changeQuantityRequestNo;

			return queue.add(() => {
				return axiosMultisite.post(`${apiUrl}changequantity`, {
					...payload,
					language: state.basketApiCulture
				})
					.then((response) => {
						if (state._changeQuantityRequestNo === currentChangeQuantityRequestNo) {
							commit('setBasket', response.data);

							const basketChangeAction = response.data.basketChangeTrackingParameters?.action;

							// public enum BasketChangeAction
							if (basketChangeAction === 1) {
								dispatch("trackRemoveFromCart", response.data);
							} else if (basketChangeAction === 0) {
								dispatch("trackAddToCart", response.data);
							}
						}
					});
			});
        },
		applyCouponBasket: ({ commit }, payload) => {
			return queue.add(() => {
				return axiosMultisite.post(`${apiUrl}apply-coupon-code`, payload)
					.then((response) => {
						commit('setBasket', response.data);
						return response;
					});
			});
		},
		removeCouponBasket: ({ commit }, payload) => {
			return queue.add(() => {
				return axiosMultisite.put(`${apiUrl}remove-coupon-code`, payload)
				.then((response) => {
					commit('setBasket', response.data);
					return response;
				});
			});
		},
		sendChangeReason: ({ state }, payload) => {
			return queue.add(() => {
				return axiosMultisite.post(setChangeReasonUrl, {
					...payload,
					language: state.basketApiCulture
				})
					.then((response) => {
						return response.data;
					})
					.catch(error => error.response);
			});
		},
		updateCuttingService: ({ commit, state }, payload) => {
			return queue.add(() => {
				return axiosMultisite.post(`${apiUrl}update-cutting-service`, {
					...payload,
					language: state.basketApiCulture
				})
				.then((response) => {
					commit('setBasket', response.data);
					return response;
				})
				.catch(error => error.response);
			});
		},

		updateE1Tax: ({ state, getters, commit }) => {
			if (state.e1TaxCalculate.abortController) {
				state.e1TaxCalculate.abortController.abort();
			}
			commit('setE1TaxCalculate', {
				abortController: new AbortController(),
				isE1TaxLoading: true,
			});

			return axiosMultisite.post(`/${getters.getE1TaxSettings.TaxUrl}`, {}, {
				signal: state.e1TaxCalculate.abortController.signal,
			})
				.then((response) => {
					commit('setE1TaxCalculate', {
						taxParams: response.data,
						isE1TaxLoading: false,
						abortController: null,
					});

					return response;
				})
				.catch((error) => {
					if (!axios.isCancel(error)) {
						commit('setE1TaxCalculate', {
							isE1TaxLoading: false,
							abortController: null,
						});
					}
					return error;
				});
		},
		updateMediLinkPartialPayment: ({ state, commit }) => {
			if (state.mediLinkPartialPaymentCalculate.abortController) {
				state.mediLinkPartialPaymentCalculate.abortController.abort();
			}

			commit('setMediLinkPartialPaymentCalculate', {
				abortController: new AbortController(),
				isLoading: true,
				apiError: null,
				paymentData: null,
			});

			return axios.get(`/${state.mediLinkPartialPaymentCalculate.apiUrl}`, {
					signal: state.mediLinkPartialPaymentCalculate.abortController.signal,
				})
				.then((response) => {
					commit('setMediLinkPartialPaymentCalculate', {
						isLoading: false,
						paymentData: response.data,
						abortController: null,
					});
					return response;
				})
				.catch((error) => {
					if (!axios.isCancel(error)) {
						commit('setMediLinkPartialPaymentCalculate', {
							isLoading: false,
							apiError: error,
							abortController: null,
						});
					}
					return error;
				});
		}
    }
};
